import { toAbsoluteUrl } from "../../_theme/helpers";
import { memo } from "react";
import HeroSection from "./Components/HeroSection";
import { useNavigate } from "react-router-dom";
import { envConfig } from "../../utils/config";
import { useAuth } from "../modules/auth";

interface Feature {
    id: number;
    text: string;
}

// Constants
const VALUES_DATA: Feature[] = [
    {
        id: 1,
        text: "Dynamic Visuals: Watch strategies unfold and evolve, revealing hidden influences and pathways to success."
    },
    {
        id: 2,
        text: "Intuitive Exploration: Move seamlessly between spatial, financial, regulatory, and stakeholder perspectives."
    },
    {
        id: 3,
        text: "Continuous Learning: Each interaction uncovers fresh perspectives, helping you refine ideas and make smarter decisions."
    },
];

const DIFFFERENCE_DATA: Feature[] = [
    {
        id: 1,
        text: "See the Big Picture – Gigamap transforms tangled data into living, breathing systems, providing a view of connections, patterns, and possibilities that are traditionally hidden.​"
    },
    {
        id: 2,
        text: "Navigate Complexity with Confidence – Tap into the power of cognitive design and critical thinking. Gigamap make it easy to understand shifting relationships, feedback loops and their causalities.​"
    },
    {
        id: 3,
        text: "Stay Ahead of the Curve - With live data overlays and scenario testing, Gigamap can provide system dynamics (how the system may evolve) for the future."
    },
    {
        id: 4,
        text: "Unlock Insights, Drive Action - Discover leverage points, identify risks, and find new opportunities for collaboration and innovation across infrastructure ecosystem."
    },
];

// Reusable list renderer
const FeatureList: React.FC<{ items: Feature[] }> = memo(({ items }) => (
    <ul className="fs-5 fw-light">
        {items.map((item) => (
            <li key={item.id} className="section-content mb-3">
                {item.text}
            </li>
        ))}
    </ul>
));

const OutcomeSection: React.FC = memo(() => (
    <div className="row d-flex flex-column-reverse flex-lg-row section-padding g-10 bg-white align-items-center m-0" id="introduction-section">
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20  pe-lg-5 pe-xl-5">
            <div>
                <p className="penoptic-evaluation-section-title mb-2">Unlock Clarity. Ignite Innovation. Shape the Future.</p>
                <p className="section-content mt-5">
                    Step into a world where complexity becomes clarity - where the unseen structures behind infrastructure planning are brought to life through intuitive, dynamic visuals. Rooted in critical design thinking and aligned with how people naturally process complexity (cognitive heuristics), these maps provide a dynamic, system-level view of the Western Cape’s infrastructure environment. Gigamap offers a backstage pass to the hidden drivers of change in the Western Cape’s infrastructure landscape.
                </p>
            </div>
        </div>
        <div className="col-md-6 text-center px-10 px-sm-15 px-md-10 px-lg-10 px-xl-20  ps-lg-5 ps-xl-5">
            <img
                src={toAbsoluteUrl("media/images/unlock-clearity.png")}
                alt="Investment-Ready Portfolio Outcome"
                className="w-100 h-100 w-xl-75 h-xl-75 rounded-circle"
                loading="lazy"
            />
        </div>
    </div>
));

const GigaMapDiffSection: React.FC = memo(() => (
    <div className="row bg-white align-items-center g-10">
        <div className="col-md-12 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20">
            <div>
                <p className="penoptic-evaluation-section-title mb-5">What Makes Gigamap Different?</p>
                <FeatureList items={DIFFFERENCE_DATA} />
            </div>
        </div>
    </div>
));

const VisionSection: React.FC = memo(() => (
    <div className="row section-padding bg-white align-items-center">
        <div className="col-md-6 text-center px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 pe-lg-5 pe-xl-5">
            <img
                src={toAbsoluteUrl("media/images/explore-gigamap.png")}
                alt="Investment-Ready Portfolio Outcome"
                className="w-100 h-100 w-xl-75 h-xl-75 rounded-circle"
                loading="lazy"
            />
        </div>
        <div className="col-md-6 px-10 px-sm-15 px-md-10 px-lg-20 px-xl-20 ps-lg-5 ps-xl-5 mt-10 mt-lg-0 ">
            <div>
                <p className="penoptic-evaluation-section-title mb-5">Explorative value of the Gigamap:</p>
                <FeatureList items={VALUES_DATA} />
                <p className="penoptic-evaluation-section-title mt-10 mb-5">Ready to See Governance in a New Light?</p>
                <p className="section-content mb-5">
                    Whether you’re a first-time planner, an investor seeking opportunity, or a policymaker ready to make an impact, Gigamap turns curiosity into action. Dive in, explore the unseen, and become part of a community shaping tomorrow’s infrastructure - one innovative step at a time.
                </p>
            </div>
        </div>
    </div>
));

// Main Component
const CriticalGigamaps: React.FC = () => {
    const { auth } = useAuth()
    const navigate = useNavigate()

    return (
        <div className="overflow-y-auto overflow-x-hidden about-us bg-white">
            <HeroSection videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/CriticalGigamapnew.mp4`}
                title="Critical Gigamaps"
                subtitle="Visualise the unseen. Lead with Critical foresight and insight."
                fallbackImage={`${envConfig.APP_MEDIA_URL}images/public-pages/CriticalGigamapnew.webp`}
                imageSrc={toAbsoluteUrl('media/images/certificate.png')}
                imageAlt="Critical Gigamaps"
                imageClassName="w-150px"
                onButtonClick={() => { auth ? window.open(`${envConfig.APP_GIGAMAP_URL}`, "_blank") : navigate("/auth/login") }}
                scrollParentId="introduction-section" />
                <OutcomeSection />
                <GigaMapDiffSection />
                <VisionSection />
        </div>
    )
};

export default CriticalGigamaps;